import {
  Raycaster,
  Matrix4,
  Object3D,
  Vector2,
  Vector3,
  BoxGeometry,
  Mesh,
  PlaneGeometry,
  Quaternion,
  SphereGeometry,
} from "three";
import { UIHandRaySystem } from "../Escy/Systems.js";
import { Object3DCmp, UIIntersectableCmp } from "../Escy/Components.js";

import { VButton } from "./UIElements/_VButton.js";
import { VContainer } from "./UIElements/_VContainer.js";
import { VText } from "./UIElements/_VText.js";
import { VProgressBar } from "./UIElements/_VProgressBar.js";
import { VProgressBar2 } from "./UIElements/_VProgressBar2.js";
import { VSlider } from "./UIElements/_VSlider.js";
import { VSlider2 } from "./UIElements/_VSlider2.js";
import { VScrollViewHonrizontal } from "./UIElements/_VScrollViewHorizontal.js";
import { VImage } from "./UIElements/_VImage.js";
import { VScrollViewVerticle } from "./UIElements/_VScrollViewVerticle.js";
import { VScrollViewGrid } from "./UIElements/_VScrollViewGrid.js";
import { VTabControl } from "./UIElements/_VTabControl.js";
import { VColorPicker } from "./UIElements/_VColorPicker.js";
import { VToggle } from "./UIElements/_VToggle.js";
import { VTree } from "./UIElements/_VTree.js";
import { ContentType, VSoftKeyboard } from "./UIElements/_VSoftKeyboard.js";
import { VDropdown } from "./UIElements/_VDropdown.js";
import { VInputField } from "./UIElements/_VInputField.js";
import { VTabControl_Text } from "./UIElements/_VTabControl_Text.js";

export class VUILib {
  constructor(camera, world, scene, isVRDevice) {
    this.camera = camera;
    this.world = world;
    this.scene = scene;
    this.raycaster = new Raycaster();
    this.tempMatrix = new Matrix4();
    this.UIs = []; // 存储所有ui
    this.isVRDevice = isVRDevice;

    this.btns = []; // 存储所有按钮
    this.containers = []; // 存储所有容器
    this.texts = []; // 存储所有文字
    this.progressBars = []; // 存储所有进度条
    this.sliders = []; // 存储所有滑动条
    this.scrollViews = []; // 存储所有的滚动视图
    this.images = []; // 存储所有图片
    this.tabControls = [];
    this.colorPockers = [];
    this.toggles = [];
    this.trees = [];
    this.dropdowns = [];
    this.inputFileds = [];

    // 按键事件，当光标停在滚动视图等UI上时可以注册按键事件，利用手柄上的上下左右按键控制滚动
    this.onLeftBtnEvent = null;
    this.onRightBtnEvent = null;
    this.onUpBtnEvent = null;
    this.onDownBtnEvent = null;

    this.pressLeft = false;
    this.pressRight = false;
    this.pressUp = false;
    this.pressDown = false;

    this.zoomCorrection = 1; // 缩放校正，UI对象在视觉上会呈现在y轴上的拉伸，需要校正

    //存放所有面板UI的对象
    this.menuUI = new Object3D();
    this.menuUI.name = "UIMenuObject";
    this.scene.add(this.menuUI);
    this.uiScale = 1;
    this.menuUI.scale.set(
      this.uiScale,
      this.uiScale * this.zoomCorrection,
      this.uiScale
    );

    // 新增缓动参数
    this.useSmooth = false; // 是否使用平滑

    this.cameraDis = -1.5;
    // 主机式设备UI距离（测试了大朋VR）
    if (!this.isVRDevice) {
      this.cameraDis = -2.8;
    }

    this.uiSmoothFactor = 5; // 提高基础平滑系数（基于帧时间计算）
    this.rotationSmoothFactor = 5; // 新增旋转专用平滑系数
    this.targetPosition = new Vector3(0, 0, this.cameraDis); // 目标位置，相对相机位置
    if (!this.useSmooth) {
      this.camera.add(this.menuUI);
      this.menuUI.position.copy(this.targetPosition);
    }

    this.globalStencil = 1; // 全局模板值，模板值的范围时[1-254]

    // 在VR设备中，UI的大小需要缩小一半
    if (this.isVRDevice) {
      this.uiScale = 0.5;
      this.menuUI.scale.set(
        this.uiScale,
        this.uiScale * this.zoomCorrection,
        this.uiScale
      );
    }
  }

  update(dt) {
    this._updateUIPosition(dt);

    this._onDragSlider();
    this._onDragContainer();

    if (this.keyboard) {
      this.keyboard.update(dt);
    }
  }

  clear() {
    for (var i = 0; i < this.UIs.length; i++) {
      this.UIs[i].clear();
    }

    this.menuUI.removeFromParent();
    this.menuUI = null;
    this.raycaster = null;
    this.tempMatrix = null;
    this.camera = null;
    this.world = null;
  }

  getCameraDis() {
    return this.cameraDis;
  }

  setCameraDis(dis) {
    this.cameraDis = dis;
    this.menuUI.position.z = this.cameraDis;
  }

  /**
   * 计算UI位置缓动
   */
  _updateUIPosition(dt) {
    if (!this.useSmooth) return;
    // 使用相机坐标系作为参考系
    const cameraWorldMatrix = new Matrix4().copy(this.camera.matrixWorld);
    const cameraRotation = new Quaternion().setFromRotationMatrix(
      cameraWorldMatrix
    );

    // 计算目标位置（始终在相机前方1.5米，使用相机坐标系）
    const targetOffset = this.targetPosition
      .clone()
      .applyQuaternion(cameraRotation);
    const targetPos = new Vector3().addVectors(
      this.camera.position,
      targetOffset
    );

    // 应用dt的位置缓动
    const delta = targetPos.clone().sub(this.menuUI.position);
    this.menuUI.position.add(delta.multiplyScalar(this.uiSmoothFactor * dt));

    // 应用dt的旋转缓动
    this.menuUI.quaternion.slerpQuaternions(
      this.menuUI.quaternion.clone(),
      cameraRotation,
      this.rotationSmoothFactor * dt
    );

    // 保持VR缩放比例
    if (this.isVRDevice) {
      const scaleFactor = this.uiScale * this.zoomCorrection;
      this.menuUI.scale.set(scaleFactor, scaleFactor, scaleFactor);
    }

    // 强制更新矩阵确保渲染同步
    this.menuUI.matrixNeedsUpdate = true;
  }

  getStencil() {
    let s = this.globalStencil;
    this.globalStencil++;
    if (this.globalStencil > 254) this.globalStencil = 1;
    return s;
  }

  /**
   * 拖拽滑动条
   */
  _onDragSlider() {
    if (this.curSlider && this.dragController) {
      let res = this._getIntersections(
        this.dragController,
        [this.curSlider.mask],
        false
      );
      //console.log(res);
      if (res && res.length > 0) {
        var point = res[0].point.clone();
        this.curSlider.setValue(point);
      }
    }
  }

  /**
   * 拖拽移动容器
   */
  _onDragContainer() {
    if (this.currentContainer && this.dragController) {
      let res = this._getIntersections(
        this.dragController,
        [this.currentContainer.mask],
        false
      );
      if (res && res.length > 0) {
        var point = res[0].point.clone();
        this.currentContainer.onMoving(point);
      }
    }
  }

  _filterUIs(array) {
    for (var i = 0; i < array.length; i++) {
      if (array[i].object.visible === false) {
        continue;
      }
      if (
        array[i].object.onEcsycheckSelectable &&
        !array[i].object.onEcsycheckSelectable(array[i].point)
      ) {
        continue;
      }
      return array[i];
    }
    return null;
  }

  /**
   * 手柄扳机键按下
   * @param {*} controller
   */
  onSelectStart(controller) {
    let res = this._getIntersections(controller, this._getTargetUIs());

    //console.log("onSelectStart", res);
    res = this._filterUIs(res);
    if (res) {
      // 检测是否要拖拽滑动条
      if (res.object && res.object._vType) {
        // slider
        if (res.object._vType === "SliderHandle") {
          this.curSlider = res.object.parent;
          this.dragController = controller;
          this.curSlider.onSelectStart();

          this.world.getSystem(UIHandRaySystem).setDragState(true);
        }
        // 拖拽移动容器位置
        else if (
          res.object._vType === "VContainer" &&
          res.object._otherProps.dragable
        ) {
          if (res.object.onSelectStart) {
            res.object.onSelectStart(res.point.clone());

            this.dragController = controller;
            this.currentContainer = res.object;
            this.world.getSystem(UIHandRaySystem).setDragState(true);
          }
        }
      }
    }
  }

  /**
   * 手柄扳机键松开  返回是否点击到了UI对象
   * @param {*} controller
   */
  onSelectEnd(controller) {
    // 拖拽
    if (this.curSlider && this.dragController === controller) {
      this.curSlider.onSelectEnd();
      this.world.getSystem(UIHandRaySystem).setDragState(false);
      this.dragController = null;
      this.curSlider = null;
      return;
    }

    // 结束拖拽容器
    if (this.currentContainer) {
      this.currentContainer.onSelectEnd();
      this.world.getSystem(UIHandRaySystem).setDragState(false);
      this.dragController = null;
      this.currentContainer = null;
      return;
    }

    let res = this._getIntersections(controller, this._getTargetUIs());
    res = this._filterUIs(res);
    //console.log(res);
    if (res) {
      if (res.object && res.object.onVRClick) {
        res.object.onVRClick(res.object, res);
      }
    }
  }

  /**
   * 更新手柄摇杆垂直方向按键值
   * @param {*} value
   */
  updateVerticleValue(value) {
    this.verticalValue = value; // 记录垂直轴的值
    if (this.verticalValue <= -0.01) {
      this.pressDown = true;
      this.pressUp = false;

      if (this.onDownBtnEvent) {
        this.onDownBtnEvent(this.verticalValue);
      }
    } else if (this.verticalValue >= 0.01) {
      this.pressUp = true;
      this.pressDown = false;

      if (this.onUpBtnEvent) {
        this.onUpBtnEvent(this.verticalValue);
      }
    } else {
      this.pressUp = false;
      this.pressDown = false;
    }
  }

  /**
   * 更新手柄水平方向按键值
   * @param {*} value
   */
  updateHorizontalValue(value) {
    this.horizontalValue = value; // 记录水平轴的值
    if (this.horizontalValue <= -0.01) {
      this.pressLeft = true;
      this.pressRight = false;

      if (this.onLeftBtnEvent) {
        this.onLeftBtnEvent(this.horizontalValue);
      }
    } else if (this.horizontalValue >= 0.01) {
      this.pressRight = true;
      this.pressLeft = false;

      if (this.onRightBtnEvent) {
        this.onRightBtnEvent(this.horizontalValue);
      }
    } else {
      this.pressLeft = false;
      this.pressRight = false;
    }
  }

  /**
   * 根据控制器进行射线检测
   * @param {*} controller
   * @param {*} objs
   * @returns
   */
  _getIntersections(controller, objs, recursive = false) {
    if (this.raycaster.setFromXRController) {
      this.raycaster.setFromXRController(controller);
    } else {
      controller.updateMatrixWorld();
      //矩阵
      this.tempMatrix
        .identity()
        .extractRotation(controller.matrixWorld.clone());
      this.raycaster.camera = this.camera;
      this.raycaster.ray.origin.setFromMatrixPosition(
        controller.matrixWorld.clone()
      );
      this.raycaster.ray.direction.set(0, 0, -1).applyMatrix4(this.tempMatrix);
    }

    let res = this.raycaster.intersectObjects(objs, recursive);

    return res;
  }

  _getTargetUIs() {
    let tempUi = [];
    this.UIs.forEach((element) => {
      tempUi = tempUi.concat(element.getClickable());
    });

    return tempUi;
  }

  /**
   *
   * @param {*} object
   * @param {*} priority 射线检测的优先级，VR按钮最高为0，其次是背景、标签、模型、2Dplayer、地面，其中模型没设置优先级
   */
  addComponents(object, priority = 0) {
    if (!object) return;
    if (
      this.world &&
      this.world.hasRegisteredComponent(Object3DCmp) &&
      this.world.hasRegisteredComponent(UIIntersectableCmp)
    ) {
      if (object.type === "Object3D" || object.type === "Group") {
        for (var i = 0; i < object.children.length; i++) {
          this.addComponents(object.children[i], priority);
        }
      } else {
        object._priority = priority;
        let entity;
        if (object._entity) {
          entity = object._entity;
        } else entity = this.world.createEntity();
        if (!entity.hasComponent(Object3DCmp))
          entity.addComponent(Object3DCmp, { object: object });
        if (!entity.hasComponent(UIIntersectableCmp))
          entity.addComponent(UIIntersectableCmp);
        object._entity = entity;
      }
    }
  }

  /**
   * 从场景中移除一个UI对象
   * @param {*} ui
   */
  removeUI(ui) {
    if (!ui) return;

    let index = this.UIs.indexOf(ui);
    if (index >= 0) {
      this.UIs.splice(index, 1);

      let targetArray = null;

      switch (ui._vType) {
        case "VButton": {
          targetArray = this.btns;
          break;
        }

        case "VContainer": {
          targetArray = this.containers;
          break;
        }
        case "VText": {
          targetArray = this.texts;
          break;
        }
        case "VImage": {
          targetArray = this.images;
          break;
        }
        case "VProgressBar":
        case "VProgressBar2": {
          targetArray = this.progressBars;
          break;
        }
        case "VSlider":
        case "VSlider2": {
          targetArray = this.sliders;
          break;
        }
        case "VScrollViewHonrizontal":
        case "VScrollViewVerticle":
        case "VScrollViewGrid": {
          targetArray = this.scrollViews;
          break;
        }
        case "VTabControl": {
          targetArray = this.tabControls;
          break;
        }
        case "VColorPicker": {
          targetArray = this.colorPockers;
          break;
        }
        case "VToggle": {
          targetArray = this.toggles;
          break;
        }
        case "VTree": {
          targetArray = this.trees;
          break;
        }
        case "VDropdown": {
          targetArray = this.dropdowns;
          break;
        }
        case "VInputField": {
          targetArray = this.inputFileds;
          break;
        }
        // other type
      }

      if (targetArray != null) {
        index = targetArray.indexOf(ui);
        if (index >= 0) {
          targetArray.splice(index, 1);
          //console.error("移除UI", ui.object.name, targetArray);
        }
      }

      if (ui.traverse) {
        let needRemoveUIs = [];
        ui.traverse((node) => {
          if (node._vType && node != ui) {
            needRemoveUIs.push(node);
          }
        });
        for (let i = 0; i < needRemoveUIs.length; i++) {
          this.removeUI(needRemoveUIs[i]);
        }
      }

      ui.clear();
    }
  }

  /**
   * 将UI对象添加到场景中
   * @param {*} uiContent
   */
  addUIToScene(uiContent) {
    if (this.menuUI) {
      this.menuUI.add(uiContent);
    }
  }

  /**
   * 显示软键盘
   * @param {String} defaultStr 原始存在的字符串内容
   * @param  {ContentType} contentType 输入的内容类型，目前支持 ContentType.Standard 标准输入，ContentType.IntegerNumber 整数，ContentType.DecimalNumber 实数
   * @param {Function} callback 回调函数，软键盘输入完成点击回车键后调用的，带一个参数，最终的字符串内容
   */
  showKeyboard(
    defaultStr,
    contentType = ContentType.Standard,
    callback = null
  ) {
    // 仅需创建一次软键盘
    if (!this.keyboard) this.createKeyboard();

    this.keyboard.show(defaultStr, contentType, callback);
  }

  /**
   * 隐藏软键盘
   */
  hideKeyboard() {
    if (this.keyboard) this.keyboard.hide();
  }

  // ----------------------------------------------------------------- 创建UI对象---------------------------------------------------
  /**
   * 创建软键盘
   */
  createKeyboard() {
    this.keyboard = new VSoftKeyboard(this);
    this.UIs.push(this.keyboard);
    this.addUIToScene(this.keyboard);
  }

  /**
   * 创建按钮，返回按钮对象
   * @param {*} options
   * @returns
   */
  createBtn(options) {
    const obj = new VButton(options, this);
    this.UIs.push(obj);
    this.btns.push(obj);
    return obj;
  }

  /**
   * 创建UI矩形容器
   * @param {*} options
   * @returns
   */
  createContainer(options) {
    const obj = new VContainer(options, this);
    this.UIs.push(obj);
    this.containers.push(obj);
    return obj;
  }

  /**
   * 创建文字
   * @param {*} options
   * @returns
   */
  createText(options) {
    const obj = new VText(options, this);
    this.UIs.push(obj);
    this.texts.push(obj);
    return obj;
  }

  /**
   * 创建图片对象
   * @param {*} options
   * @returns
   */
  createImage(options) {
    const obj = new VImage(options, this);
    this.UIs.push(obj);
    this.images.push(obj);
    return obj;
  }

  /**
   * 创建矩形进度条
   * @param {*} options
   * @returns
   */
  createProgressBar(options) {
    const obj = new VProgressBar(options, this);
    this.UIs.push(obj);
    this.progressBars.push(obj);
    return obj;
  }

  /**
   * 创建环形进度条
   * @param {*} options
   * @returns
   */
  createProgressBarRing(options) {
    const obj = new VProgressBar2(options, this);
    this.UIs.push(obj);
    this.progressBars.push(obj);
    return obj;
  }

  /**
   * 创建带有填充的滑动条，如音量滑动条
   * @param {*} options
   * @returns
   */
  createSlider(options) {
    const obj = new VSlider(options, this);
    this.UIs.push(obj);
    this.sliders.push(obj);
    return obj;
  }

  /**
   * 创建没有填充的滑动条，通常用于滚动区域
   * @param {*} options
   * @returns
   */
  createSlider2(options) {
    const obj = new VSlider2(options, this);
    this.UIs.push(obj);
    this.sliders.push(obj);
    return obj;
  }

  /**
   * 创建水平滚动视图
   * @param {*} options
   * @returns
   */
  createScrollViewHorizontal(options) {
    const obj = new VScrollViewHonrizontal(options, this);
    this.UIs.push(obj);
    this.scrollViews.push(obj);
    return obj;
  }

  /**
   * 创建垂直滚动视图
   * @param {*} options
   * @returns
   */
  createScrollViewVerticle(options) {
    const obj = new VScrollViewVerticle(options, this);
    this.UIs.push(obj);
    this.scrollViews.push(obj);
    return obj;
  }

  /**
   * 创建网格滚动视图
   * @param {*} options
   * @returns
   */
  createScrollViewGrid(options) {
    const obj = new VScrollViewGrid(options, this);
    this.UIs.push(obj);
    this.scrollViews.push(obj);
    return obj;
  }

  /**
   * 创建按钮组用于分页控制
   * @param {*} options
   * @returns
   */
  createTabControl(options) {
    const obj = new VTabControl(options, this);
    this.UIs.push(obj);
    this.tabControls.push(obj);
    return obj;
  }

  /**
   * 创建纯文字的按钮组
   * @param {*} options
   * @returns
   */
  createTabControl2(options) {
    const obj = new VTabControl_Text(options, this);
    this.UIs.push(obj);
    this.tabControls.push(obj);
    return obj;
  }

  /**
   * 创建拾色器
   * @param {*} options
   * @returns
   */
  createColorPicker(options) {
    const obj = new VColorPicker(options, this);
    this.UIs.push(obj);
    this.colorPockers.push(obj);
    return obj;
  }

  /**
   * 创建toggle
   * @param {*} options
   * @returns
   */
  createToggle(options) {
    const obj = new VToggle(options, this);
    this.UIs.push(obj);
    this.toggles.push(obj);
    return obj;
  }

  /**
   * 创建树
   * @param {*} options
   * @returns
   */
  createTree(options) {
    const obj = new VTree(options, this);
    this.UIs.push(obj);
    this.trees.push(obj);
    return obj;
  }

  /**
   * 创建下拉列表
   * @param {*} options
   * @returns
   */
  createDropdown(options) {
    const obj = new VDropdown(options, this);
    this.UIs.push(obj);
    this.dropdowns.push(obj);
    return obj;
  }

  createInputField(options) {
    const obj = new VInputField(options, this);
    this.UIs.push(obj);
    this.inputFileds.push(obj);
    return obj;
  }
}
